// GENERATED by blackbird_backend/scripts/tools/generate-discipline-registry.mjs. Do not edit.
// Source: blackbird_backend/src/layers/shared-utils/nodejs/clinical/noteTemplates.js (Phase 20E)

export type NoteFieldType = 'text' | 'textarea' | 'select' | 'multiselect' | 'number' | 'boolean';

export interface NoteTemplateField {
  key: string;
  name: string;
  type: NoteFieldType;
  required?: boolean;
  options?: readonly string[];
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface NoteTemplate {
  entryType: string;
  label: string;
  disciplines: readonly string[];
  /** Phase 20F: offered only while the patient is in this programme (palliative) */
  program?: string;
  fields: readonly NoteTemplateField[];
}

export const NOTE_TEMPLATES: readonly NoteTemplate[] = [
  {
    "entryType": "PSYCHOSOCIAL_NOTE",
    "label": "Social work note",
    "disciplines": [
      "MSW"
    ],
    "fields": [
      {
        "key": "focus",
        "name": "Focus",
        "type": "select",
        "required": true,
        "options": [
          "Caregiver support",
          "Benefits and resources",
          "Crisis intervention",
          "Discharge planning",
          "Advance care planning",
          "Family adjustment",
          "Other"
        ]
      },
      {
        "key": "interventions",
        "name": "Interventions",
        "type": "textarea",
        "required": true
      },
      {
        "key": "resources",
        "name": "Resources discussed",
        "type": "multiselect",
        "options": [
          "Medicaid or waiver application",
          "SSI",
          "Respite",
          "Support group",
          "Transportation",
          "Housing",
          "Food resources",
          "School or ECI",
          "Legal aid",
          "Counseling referral"
        ]
      },
      {
        "key": "referralsMade",
        "name": "Referrals made",
        "type": "textarea"
      },
      {
        "key": "plan",
        "name": "Plan",
        "type": "textarea",
        "required": true
      }
    ]
  },
  {
    "entryType": "RT_TREATMENT",
    "label": "Respiratory treatment",
    "disciplines": [
      "RT"
    ],
    "fields": [
      {
        "key": "treatments",
        "name": "Treatments given",
        "type": "multiselect",
        "required": true,
        "options": [
          "Nebulizer treatment",
          "Airway clearance or chest physiotherapy",
          "Cough assist",
          "Suctioning",
          "Ventilator check",
          "Tracheostomy change",
          "Tracheostomy care",
          "Oxygen titration",
          "Equipment check",
          "Asthma education"
        ]
      },
      {
        "key": "preSpo2",
        "name": "SpO2 before (%)",
        "type": "number",
        "min": 50,
        "max": 100
      },
      {
        "key": "postSpo2",
        "name": "SpO2 after (%)",
        "type": "number",
        "min": 50,
        "max": 100
      },
      {
        "key": "preRespRate",
        "name": "Respiratory rate before",
        "type": "number",
        "min": 5,
        "max": 120
      },
      {
        "key": "postRespRate",
        "name": "Respiratory rate after",
        "type": "number",
        "min": 5,
        "max": 120
      },
      {
        "key": "breathSounds",
        "name": "Breath sounds before and after",
        "type": "textarea"
      },
      {
        "key": "ventSettingsVerified",
        "name": "Ventilator settings match the order",
        "type": "boolean"
      },
      {
        "key": "education",
        "name": "Education given",
        "type": "textarea"
      },
      {
        "key": "response",
        "name": "Patient response",
        "type": "textarea",
        "required": true
      }
    ]
  },
  {
    "entryType": "NUTRITION_NOTE",
    "label": "Nutrition note",
    "disciplines": [
      "RD"
    ],
    "fields": [
      {
        "key": "weight",
        "name": "Weight",
        "type": "text",
        "placeholder": "kg"
      },
      {
        "key": "intakeReview",
        "name": "Intake review",
        "type": "textarea",
        "required": true
      },
      {
        "key": "tolerance",
        "name": "Tolerance",
        "type": "multiselect",
        "options": [
          "Tolerating well",
          "Vomiting",
          "Reflux",
          "Diarrhea",
          "Constipation",
          "Abdominal distension",
          "Feeding refusal"
        ]
      },
      {
        "key": "formulaChanges",
        "name": "Formula or regimen changes recommended",
        "type": "textarea"
      },
      {
        "key": "physicianNotified",
        "name": "Physician notified of recommendations",
        "type": "boolean"
      },
      {
        "key": "plan",
        "name": "Plan",
        "type": "textarea",
        "required": true
      }
    ]
  },
  {
    "entryType": "BH_SESSION",
    "label": "Behavioral session note",
    "disciplines": [
      "BH"
    ],
    "fields": [
      {
        "key": "sessionType",
        "name": "Session type",
        "type": "select",
        "required": true,
        "options": [
          "Direct therapy",
          "Caregiver training",
          "Assessment",
          "Supervision of a technician",
          "Team meeting"
        ]
      },
      {
        "key": "targetsAddressed",
        "name": "Targets addressed",
        "type": "textarea",
        "required": true
      },
      {
        "key": "sessionData",
        "name": "Session data",
        "type": "textarea",
        "required": true,
        "placeholder": "Counts, durations, percent correct"
      },
      {
        "key": "caregiverTraining",
        "name": "Caregiver training",
        "type": "textarea"
      },
      {
        "key": "incidents",
        "name": "Behavior incidents",
        "type": "textarea"
      },
      {
        "key": "plan",
        "name": "Plan",
        "type": "textarea",
        "required": true
      }
    ]
  },
  {
    "entryType": "SYMPTOM",
    "label": "Symptom assessment",
    "disciplines": [
      "RN"
    ],
    "program": "palliative",
    "fields": [
      {
        "key": "painScore",
        "name": "Pain (0–10)",
        "type": "number",
        "min": 0,
        "max": 10
      },
      {
        "key": "dyspnea",
        "name": "Breathlessness",
        "type": "select",
        "options": [
          "None",
          "Mild",
          "Moderate",
          "Severe"
        ]
      },
      {
        "key": "nausea",
        "name": "Nausea",
        "type": "select",
        "options": [
          "None",
          "Mild",
          "Moderate",
          "Severe"
        ]
      },
      {
        "key": "agitation",
        "name": "Agitation",
        "type": "select",
        "options": [
          "None",
          "Mild",
          "Moderate",
          "Severe"
        ]
      },
      {
        "key": "secretions",
        "name": "Secretions",
        "type": "select",
        "options": [
          "None",
          "Small",
          "Moderate",
          "Large"
        ]
      },
      {
        "key": "sleep",
        "name": "Sleep",
        "type": "select",
        "options": [
          "Good",
          "Fair",
          "Poor"
        ]
      },
      {
        "key": "interventions",
        "name": "Comfort interventions",
        "type": "textarea",
        "required": true
      },
      {
        "key": "comfortGoalMet",
        "name": "Comfort goal met",
        "type": "select",
        "required": true,
        "options": [
          "Yes",
          "Partly",
          "No"
        ]
      }
    ]
  }
];
